/**
 * Logique de l'écran « Apps mobiles » : lire un brouillon de champs, le
 * valider avec les règles de `@artosera/shared` (les mêmes que l'API), et dire
 * si l'enregistrement relève la version minimale. Pure, donc testée par l'écran.
 */
import {
  CONSEILLEE_COHERENTE,
  comparerVersions,
  exigencesPlateformeSchema,
  versionAppSchema,
  type ExigencesPlateforme,
} from '@artosera/shared';
import { ApiError } from './api.js';

/** Les trois champs tels qu'on les saisit : vide = aucune exigence. */
export interface BrouillonVersions {
  minimale: string;
  conseillee: string;
  urlBoutique: string;
}

export type ChampVersions = keyof BrouillonVersions;
export type ErreurChamp = 'version' | 'url' | 'conseilleeSousMinimale';
export type ErreursChamps = Partial<Record<ChampVersions, ErreurChamp>>;

export function brouillonDe(exigence: ExigencesPlateforme): BrouillonVersions {
  return {
    minimale: exigence.minimale ?? '',
    conseillee: exigence.conseillee ?? '',
    urlBoutique: exigence.urlBoutique ?? '',
  };
}

const ouNull = (texte: string): string | null => (texte.trim() === '' ? null : texte.trim());

/** Les champs lus comme l'API les attend : vide devient `null`. */
export function reglageDe(brouillon: BrouillonVersions): ExigencesPlateforme {
  return {
    minimale: ouNull(brouillon.minimale),
    conseillee: ouNull(brouillon.conseillee),
    urlBoutique: ouNull(brouillon.urlBoutique),
  };
}

export function estModifie(brouillon: BrouillonVersions, exigence: ExigencesPlateforme): boolean {
  const lu = reglageDe(brouillon);
  return (
    lu.minimale !== exigence.minimale ||
    lu.conseillee !== exigence.conseillee ||
    lu.urlBoutique !== exigence.urlBoutique
  );
}

/** Les champs refusés, un code par champ ; vide si le réglage est envoyable. */
export function erreursDe(brouillon: BrouillonVersions): ErreursChamps {
  const lu = reglageDe(brouillon);
  const erreurs: ErreursChamps = {};
  if (lu.minimale !== null && !versionAppSchema.safeParse(lu.minimale).success) {
    erreurs.minimale = 'version';
  }
  if (lu.conseillee !== null && !versionAppSchema.safeParse(lu.conseillee).success) {
    erreurs.conseillee = 'version';
  }
  if (!exigencesPlateformeSchema.shape.urlBoutique.safeParse(lu.urlBoutique).success) {
    erreurs.urlBoutique = 'url';
  }
  if (!erreurs.minimale && !erreurs.conseillee && !CONSEILLEE_COHERENTE[0](lu)) {
    erreurs.conseillee = 'conseilleeSousMinimale';
  }
  return erreurs;
}

/** La minimale monte (ou apparaît) : des apps jusque-là admises vont être bloquées. */
export function releveLaMinimale(
  brouillon: BrouillonVersions,
  exigence: ExigencesPlateforme,
): boolean {
  const apres = ouNull(brouillon.minimale);
  if (apres === null) return false;
  return exigence.minimale === null || comparerVersions(apres, exigence.minimale) > 0;
}

/** 409 : les versions ont bougé depuis la lecture (`revision_versions_perimee`). */
export function estRevisionPerimee(erreur: unknown): boolean {
  return erreur instanceof ApiError && erreur.status === 409;
}
